/* Attributed + Overhead = Organisation — STORY-023.
 *
 * ── the arithmetic is the product ───────────────────────────────────────────
 * A spreadsheet already has these numbers. What it does not have is the middle
 * line explaining why per-event profit and business profit differ, so the three
 * figures are shown TOGETHER and labelled. Showing two of them and deriving the
 * third on screen would put the reader back where they started.
 *
 * ── the relationship is stated in text, never implied by layout ─────────────
 * The story asks for this by name, and it is an accessibility requirement: a
 * column order communicates nothing to a screen reader, and "these add up" is
 * the one thing this screen exists to say. So each row carries a sentence
 * saying it, and the figures are a table with headers by name.
 *
 * ── overhead is never an unexplained lump ───────────────────────────────────
 * The figure opens onto the Transactions composing it. A number somebody
 * cannot check is a number they have to trust, and this whole screen is an
 * argument against trusting numbers.
 *
 * ── a loss is words, not colour ─────────────────────────────────────────────
 * `design.md` forbids colour carrying meaning alone, and overhead is very often
 * negative — so this is the common case here, not the edge one.
 */

import { formatMoney, money, parseCurrency } from '@eventuley/domain';
import { useState } from 'react';
import { ApiError, NetworkError } from '../../../shared/api/client.js';
import { useI18n, useTranslate } from '../../../shared/i18n/index.js';
import { Button, Callout } from '../../../shared/ui/index.js';
import { useLedger, useReconciliation } from '../api/index.js';

export function Reconciliation({ organisationId }: { readonly organisationId: string }) {
  const t = useTranslate();
  const { formatLocale: locale } = useI18n();
  const { data, error, isLoading } = useReconciliation(organisationId);
  /* WHICH currency's overhead is open, not merely whether one is.
   *
   * A single button under a table whose every figure is per currency produced
   * a list mixing them, adding up to no figure on the screen that opened it.
   * That is worse than no drill-down: the panel invites the reader to add the
   * list up and check, and then fails the check. */
  const [openCurrency, setOpenCurrency] = useState<string | null>(null);

  /**
   * Minor units to something a person reads, in their own locale.
   *
   * The digit count comes from the CURRENCY, never a constant. A code the
   * runtime does not know shows the bare number and the code rather than a
   * guessed symbol — a wrong symbol on a real figure is worse than none.
   */
  const format = (minor: number, currency: string) => {
    const code = parseCurrency(currency);
    return code ? formatMoney(money(minor, code), locale) : `${minor} ${currency}`;
  };

  /** A negative figure said in words, because colour is not enough. */
  const signed = (minor: number, currency: string) =>
    minor < 0
      ? t('reconciliation.down', { amount: format(Math.abs(minor), currency) })
      : format(minor, currency);

  if (error) {
    /* The reconciliation-specific wording is reserved for a server fault.
       A ReconciliationError arrives as a 500 — the endpoint refuses to answer
       with figures that do not add up, so there is nothing to fall back to and
       saying why is the right thing to do.

       Everything else must not claim that. A session that expired or a
       Membership removed in another tab arrive as 401 and 404, and telling
       that reader their arithmetic is broken and has been reported is both
       false and unactionable. */
    const serverFault = error instanceof ApiError && error.status >= 500;

    return (
      <section className="settings__section">
        <h2>{t('reconciliation.heading')}</h2>
        <Callout tone="danger">
          {error instanceof NetworkError
            ? t('error.network')
            : serverFault
              ? t('reconciliation.unavailable')
              : t('error.unexpected')}
        </Callout>
      </section>
    );
  }

  if (isLoading || !data) {
    return (
      <section className="settings__section">
        <h2>{t('reconciliation.heading')}</h2>
        <p className="field__hint">{t('common.loading')}</p>
      </section>
    );
  }

  return (
    <section className="settings__section">
      <h2>{t('reconciliation.heading')}</h2>
      <p className="field__hint">{t('reconciliation.intro')}</p>

      {data.figures.length === 0 ? (
        /* Zero, not blank. A new workspace has recorded nothing and must not
           read as something having gone wrong. */
        <p className="field__hint">{t('reconciliation.nothing_yet')}</p>
      ) : (
        <>
          <div className="table-wrap">
            <table className="data-table">
              <caption className="sr-only">{t('reconciliation.table_caption')}</caption>
              <thead>
                <tr>
                  <th scope="col">{t('reconciliation.currency_column')}</th>
                  <th scope="col" className="num">
                    {t('reconciliation.attributed_column')}
                  </th>
                  <th scope="col" className="num">
                    {t('reconciliation.overhead_column')}
                  </th>
                  <th scope="col" className="num">
                    {t('reconciliation.organisation_column')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.figures.map((figure) => (
                  <tr key={figure.currency}>
                    <th scope="row">{figure.currency}</th>
                    <td className="num">{signed(figure.attributedMinor, figure.currency)}</td>
                    <td className="num">{signed(figure.overheadMinor, figure.currency)}</td>
                    <td className="num">{signed(figure.organisationMinor, figure.currency)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* The sentence a column order cannot say, and the way into the
              entries behind it. One of each per currency, because the identity
              holds within a currency and nowhere across them. */}
          {data.figures.map((figure) => (
            <div key={`${figure.currency}-sentence`}>
              <p className="field__hint">
                {/* Two whole sentences chosen by the sign, never one assembled
                  from parts — `i18n.md` forbids the concatenation, and
                  Turkish attaches its suffixes to the word. Overhead is
                  usually a cost and sometimes a receipt, and "plus minus two
                  hundred" is not a sentence in any language. */}
                {t(
                  figure.overheadMinor < 0
                    ? 'reconciliation.adds_up_cost'
                    : 'reconciliation.adds_up_income',
                  {
                    currency: figure.currency,
                    attributed: signed(figure.attributedMinor, figure.currency),
                    overhead: format(Math.abs(figure.overheadMinor), figure.currency),
                    organisation: signed(figure.organisationMinor, figure.currency),
                  },
                )}
              </p>

              <div className="btn-row">
                <Button
                  variant="ghost"
                  aria-expanded={openCurrency === figure.currency}
                  onClick={() =>
                    setOpenCurrency(openCurrency === figure.currency ? null : figure.currency)
                  }
                >
                  {openCurrency === figure.currency
                    ? t('reconciliation.hide_overhead', { currency: figure.currency })
                    : t('reconciliation.show_overhead', { currency: figure.currency })}
                </Button>
              </div>

              {openCurrency === figure.currency ? (
                <OverheadEntries organisationId={organisationId} currency={figure.currency} />
              ) : null}
            </div>
          ))}
        </>
      )}
    </section>
  );
}

/**
 * What ONE currency's Overhead figure is made of.
 *
 * The ledger, filtered to Transactions naming no Event — the same endpoint and
 * the same rows the figure was summed from, rather than a second query shaped
 * differently. Two paths to one number is how they come to disagree.
 */
function OverheadEntries({
  organisationId,
  currency,
}: {
  readonly organisationId: string;
  readonly currency: string;
}) {
  const t = useTranslate();
  const { formatLocale: locale } = useI18n();
  /* Scoped to the one currency whose figure was clicked. Without it the list
   * mixed currencies and its count spanned them, so adding it up matched no
   * figure above — the exact check this panel exists to invite. */
  const { data, error, isLoading } = useLedger(organisationId, {
    attribution: 'overhead',
    currency,
  });

  const format = (minor: number, code: string) => {
    const parsed = parseCurrency(code);
    return parsed ? formatMoney(money(minor, parsed), locale) : `${minor} ${code}`;
  };

  if (error) {
    return (
      <Callout tone="danger">
        {error instanceof NetworkError ? t('error.network') : t('error.unexpected')}
      </Callout>
    );
  }

  if (isLoading || !data) return <p className="field__hint">{t('common.loading')}</p>;

  if (data.transactions.length === 0) {
    return <p className="field__hint">{t('reconciliation.no_overhead')}</p>;
  }

  return (
    <ul className="row-list">
      {data.transactions.map((transaction) => (
        <li className="row" key={transaction.id}>
          <span className="row__main">
            <span className="row__title">{transaction.description}</span>
            <span className="row__meta">{transaction.category.name}</span>
          </span>
          <span className="row__actions">
            <span className="row__figure">
              {transaction.direction === 'income'
                ? t('money.amount_in', {
                    amount: format(transaction.amountMinor, transaction.currency),
                  })
                : t('money.amount_out', {
                    amount: format(transaction.amountMinor, transaction.currency),
                  })}
            </span>
          </span>
        </li>
      ))}
      {/* Said out loud when the list is only part of what the figure covers.
          The ledger pages; the figure does not. A reader adding up a page and
          finding it short of the total would be right to distrust both. */}
      {data.total > data.transactions.length ? (
        <li className="row">
          <span className="row__main">
            <span className="row__meta">
              {/* Numbers, not strings. ICU cannot number-format a string, so
                  a Turkish workspace with 1 500 entries would read "1500"
                  where the locale wants "1.500" — and a plural category could
                  never be added to the message later. */}
              {t('reconciliation.overhead_truncated', {
                count: data.transactions.length,
                total: data.total,
              })}
            </span>
          </span>
        </li>
      ) : null}
    </ul>
  );
}
